/**
 * Wave DOC (decision 0068, spec `docs/design/documents-2026-10-12/spec.md` §2.1): a document's place in its project.
 *
 * Pure and dependency-free (no `effect` import): the user-content Worker imports it as `@minga/protocol/doc-path`
 * without bundling Effect. `documents.ts` wraps these in schemas.
 *
 * A path is NFC, uses "/" only, has no leading "/", no "." or ".." segment (also not percent-encoded), no empty
 * segment, no "\\", "?" or "#", no control characters and no Unicode format characters (category Cf: zero-width
 * characters, bidi controls), ≤ 300 characters and ≤ 120 per segment.
 */

export const DOC_PATH_MAX = 300
export const DOC_SEGMENT_MAX = 120

// C0, DEL and C1 controls.
// biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what this rejects
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/
// Unicode format characters: U+200B-200F, U+202A-202E, U+2066-2069, U+FEFF, soft hyphen, …
const FORMAT = /\p{Cf}/u

/** A segment's percent-decoded form ("%2e%2e" → ".."); the raw segment when it does not decode. */
const decodedSegment = (seg: string): string => {
  if (!seg.includes("%")) return seg
  try {
    return decodeURIComponent(seg)
  } catch {
    return seg
  }
}

const isDotSegment = (seg: string): boolean => {
  const d = decodedSegment(seg)
  return d === "." || d === ".."
}

/** Does `p` follow every rule of a document path? */
export const isDocPath = (p: string): boolean => {
  if (p.length === 0 || p.length > DOC_PATH_MAX) return false
  if (p.normalize("NFC") !== p) return false
  if (p.startsWith("/") || p.includes("\\") || p.includes("?") || p.includes("#")) return false
  if (CONTROL.test(p) || FORMAT.test(p)) return false
  for (const seg of p.split("/")) {
    if (seg.length === 0 || seg.length > DOC_SEGMENT_MAX) return false
    if (isDotSegment(seg)) return false
    // an encoded "/" or "\\" would change the path once a URL is decoded
    const d = decodedSegment(seg)
    if (d !== seg && (d.includes("/") || d.includes("\\"))) return false
  }
  return true
}

/**
 * A path as typed or walked ("./docs\\x/README.md ") → its normal form ("docs/x/README.md"), or null when it breaks a
 * rule. NFC, trimmed, "\\" → "/", "." segments dropped. Everything else (a leading "/", "..", an empty segment) is
 * refused, not repaired.
 */
export const normalizeDocPath = (raw: string): string | null => {
  const p = raw.normalize("NFC").trim().replaceAll("\\", "/")
  if (p.startsWith("/")) return null
  const segs = p.split("/").filter((s) => s !== ".")
  const out = segs.join("/")
  return isDocPath(out) ? out : null
}

/** The uniqueness key: the path lowercased (README.md and readme.md are the same document). */
export const docPathKey = (path: string): string => path.toLowerCase()

/** The folder of a path ("a/b/c.md" → "a/b"; "c.md" → ""). */
export const docFolderOf = (path: string): string => {
  const i = path.lastIndexOf("/")
  return i < 0 ? "" : path.slice(0, i)
}

/** Is `path` under `folder` ("" is the root, which holds everything)? Case-insensitive, like {@link docPathKey}. */
export const isUnderDocFolder = (path: string, folder: string): boolean =>
  folder === "" || docPathKey(path).startsWith(`${docPathKey(folder)}/`)

const SCHEME = /^[a-z][a-z0-9+.-]*:/i

/**
 * Resolve a relative href ("../map.md", "shots/a.png#x", "#top") from a document's path. `hash` is "" or starts with
 * "#"; a query ("?x") is dropped. Null when the href is absolute (a scheme, "//host", "/path"), leaves the root, does
 * not decode, or resolves to something that is not a document path.
 */
export const resolveDocPath = (fromPath: string, href: string): { path: string; hash: string } | null => {
  const raw = href.trim().replaceAll("\\", "/")
  const hashAt = raw.indexOf("#")
  const hash = hashAt < 0 ? "" : raw.slice(hashAt)
  let rel = hashAt < 0 ? raw : raw.slice(0, hashAt)
  const queryAt = rel.indexOf("?")
  if (queryAt >= 0) rel = rel.slice(0, queryAt)
  if (SCHEME.test(rel) || rel.startsWith("/")) return null
  if (rel === "") return isDocPath(fromPath) ? { path: fromPath, hash } : null
  const stack = fromPath.split("/").slice(0, -1)
  for (const seg of rel.split("/")) {
    let d: string
    try {
      d = decodeURIComponent(seg)
    } catch {
      return null
    }
    if (d === "" || d === ".") continue
    if (d === "..") {
      if (stack.length === 0) return null
      stack.pop()
      continue
    }
    if (d.includes("/")) return null
    stack.push(d)
  }
  const path = stack.join("/").normalize("NFC")
  return isDocPath(path) ? { path, hash } : null
}
